import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { ArrowUpRight, Sparkles, Image as ImageIcon, CalendarCheck, Mail } from "lucide-react";

const STEPS = [
  {
    icon: Sparkles,
    kicker: "01",
    title: "Get scouted, first.",
    body:
      "Fill the swipe-through profile with your name, photos and a short intro. Reviewed by hand — usually within a few days.",
  },
  {
    icon: ImageIcon,
    kicker: "02",
    title: "Build a mood board.",
    body:
      "A mood board is a small collection of visual references — magazine tears, campaign frames, screenshots of shoots you love. It tells us the vibe you want.",
  },
  {
    icon: CalendarCheck,
    kicker: "03",
    title: "Pick the dates you're free.",
    body:
      "Add a few open dates and let us know your preferred style (editorial, beauty, portrait). Our creative team weighs each submission carefully.",
  },
  {
    icon: Mail,
    kicker: "04",
    title: "Watch your inbox.",
    body:
      "If your mood board gets the green light, you'll receive an email confirming the shoot date, location and full call sheet. No cost. Frames yours to keep.",
  },
];

export default function GettingStarted() {
  return (
    <section
      id="getting-started"
      data-testid="getting-started-section"
      className="bg-[#ffffff] text-[#0a0a0a] py-24 md:py-32 px-6 md:px-10 border-t border-[#0a0a0a]/10"
    >
      <div className="max-w-[1400px] mx-auto">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16 md:mb-20">
            <div className="max-w-2xl">
              <div className="flex items-center gap-4 mb-6">
                <span className="h-px w-12 bg-[#000000]" />
                <span className="text-xs uppercase tracking-[0.3em] font-bold text-[#737373]">
                  Getting Started
                </span>
              </div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-none mb-6">
                Your first shoot, <span className="italic">on us</span>.
              </h2>
              <p className="font-script text-[#0a0a0a] text-4xl md:text-5xl leading-none mb-6">
                welcome to VOGUEVERVE.
              </p>
              <p className="text-base md:text-lg text-[#0a0a0a]/60 leading-relaxed max-w-xl">
                Every new signup on the platform is eligible for a free editorial shoot. Here&rsquo;s the whole thing in four honest steps.
              </p>
            </div>
            <Link
              to="/free-shoot"
              data-testid="gs-cta-freeshoot"
              className="hidden md:inline-flex items-center gap-3 rounded-full bg-[#000000] text-white text-xs uppercase tracking-[0.25em] font-bold px-7 py-4 hover:bg-[#1f1f1f] transition-colors"
            >
              Claim my free shoot <ArrowUpRight size={14} strokeWidth={2.2} />
            </Link>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#0a0a0a]/10 border border-[#0a0a0a]/10">
          {STEPS.map((s, i) => (
            <Reveal key={s.kicker} delay={i * 0.08}>
              <div className="bg-white p-8 md:p-10 h-full flex flex-col">
                <div className="flex items-center justify-between mb-8">
                  <span className="text-[10px] uppercase tracking-[0.35em] font-bold text-[#0a0a0a]/40 tabular-nums">
                    {s.kicker}
                  </span>
                  <s.icon size={18} strokeWidth={1.6} className="text-[#0a0a0a]/60" />
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-light tracking-tight leading-tight mb-4">
                  {s.title}
                </h3>
                <p className="text-sm md:text-base text-[#0a0a0a]/60 leading-relaxed">
                  {s.body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-16 md:mt-24 flex flex-col md:flex-row items-start md:items-center justify-between gap-8 border-t border-[#0a0a0a]/10 pt-10">
            <div className="max-w-2xl">
              <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#0a0a0a]/50 mb-3">
                Not sure what a mood board looks like?
              </p>
              <p className="text-base md:text-lg text-[#0a0a0a]/70 leading-relaxed">
                Think of a Pinterest board, condensed. Six to ten images that answer one question — <em>what do I want the shoot to feel like?</em> Screenshots from Vogue, Numéro, Dazed, CR Fashion Book, or campaign frames from Miu Miu, Chloé, or JW Anderson — anything that captures the mood.
              </p>
            </div>
            <Link
              to="/free-shoot"
              data-testid="gs-cta-freeshoot-mobile"
              className="md:hidden inline-flex items-center gap-3 rounded-full bg-[#000000] text-white text-xs uppercase tracking-[0.25em] font-bold px-7 py-4 hover:bg-[#1f1f1f] transition-colors"
            >
              Claim my free shoot <ArrowUpRight size={14} strokeWidth={2.2} />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
